import React, { useState, useEffect } from 'react';
import {
    Grid,
    Button,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    DialogContent
} from '@mui/material';
import { Add } from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import RootDialogPage from '../../Commun/components/RootDialogPage';
import { tripsApi, Trip } from '../services/tripsApi';
import { vehiclesApi, Vehicle } from '../services/vehiclesApi';
import { driversApi, Driver } from '../services/driversApi';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';

const Trips: React.FC = () => {
    const { t } = useLanguage();
    
    const [trips, setTrips] = useState<Trip[]>([]);
    const [vehicles, setVehicles] = useState<Vehicle[]>([]);
    const [drivers, setDrivers] = useState<Driver[]>([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingTrip, setEditingTrip] = useState<Trip | null>(null);
    const [formData, setFormData] = useState<Partial<Trip>>({
        title: '',
        purpose: 'business',
        status: 'scheduled',
        vehicleId: 0,
        driverId: 0
    });

    const loadData = async () => {
        setLoading(true);
        try {
            const [tRes, vRes, dRes] = await Promise.all([
                tripsApi.getAll(),
                vehiclesApi.getAll(),
                driversApi.getAll()
            ]);

            if (tRes.success) setTrips(tRes.data);
            if (vRes.success) setVehicles(vRes.data);
            if (dRes.success) setDrivers(dRes.data);

            if (!tRes.success) blobticMessage.error(t('common.messages.error') || 'Error', tRes.message || 'Unknown error');
        } catch (error) {
            console.error('Failed to load trips data:', error);
            blobticMessage.error(t('common.messages.error'), 'Failed to load trips data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleOpenDialog = (trip?: Trip) => {
        if (trip) {
            setEditingTrip(trip);
            setFormData({ ...trip });
        } else {
            setEditingTrip(null);
            setFormData({
                title: '',
                purpose: 'business',
                status: 'scheduled',
                vehicleId: vehicles.length > 0 ? vehicles[0].id : 0,
                driverId: drivers.length > 0 ? drivers[0].id : 0
            });
        }
        setIsDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            let response;
            if (editingTrip) {
                response = await tripsApi.update(editingTrip.id, formData);
            } else {
                response = await tripsApi.create(formData);
            }

            if (response.success) {
                blobticMessage.success(t('common.messages.success'), t('common.messages.saved'));
                setIsDialogOpen(false);
                loadData();
            } else {
                blobticMessage.error(t('common.messages.error') || 'Error', response.message || 'Unknown error');
            }
        } catch (error) {
            console.error('Error saving trip:', error);
            blobticMessage.error(t('common.messages.error'), t('common.messages.error'));
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm(t('common.actions.deleteConfirm'))) return;
        try {
            const response = await tripsApi.delete(id);
            if (response.success) {
                blobticMessage.success(t('common.messages.success'), t('common.messages.deleted'));
                loadData();
            } else {
                blobticMessage.error(t('common.messages.error') || 'Error', response.message || 'Unknown error');
            }
        } catch (error) {
            blobticMessage.error(t('common.messages.error') || 'Error', t('common.messages.error') || 'Unknown error');
        }
    };

    const columns = [
        { id: 'title', label: t('parc.trips.tripTitle'), minWidth: 150 },
        { id: 'vehicleRegistration', label: t('parc.trips.vehicle'), minWidth: 120 },
        { id: 'driverLicenseNumber', label: t('parc.trips.driver'), minWidth: 120 },
        {
            id: 'purpose',
            label: t('parc.trips.purpose'),
            minWidth: 100,
            format: (value: string) => {
                if (value === 'business') return t('parc.trips.purposeBusiness');
                if (value === 'personal') return t('parc.trips.purposePersonal');
                if (value === 'maintenance') return t('parc.trips.purposeMaintenance');
                if (value === 'delivery') return t('parc.trips.purposeDelivery');
                if (value === 'other') return t('parc.trips.purposeOther');
                return value;
            }
        },
        {
            id: 'status',
            label: t('parc.trips.status'),
            minWidth: 100,
            format: (value: string) => {
                if (value === 'scheduled') return t('parc.trips.statusScheduled');
                if (value === 'in_progress') return t('parc.trips.statusInProgress');
                if (value === 'completed') return t('parc.trips.statusCompleted');
                if (value === 'cancelled') return t('parc.trips.statusCancelled');
                return value;
            }
        }
    ];

    const additionalActions = (
        <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
        >
            {t('parc.trips.newTrip')}
        </Button>
    );

    return (
        <GenericPage title={t('parc.trips.title')} additionalActions={additionalActions} footerEnabled={false} disableScroll>
            <GenericTable
                columns={columns}
                data={trips}
                onEdit={handleOpenDialog}
                onDelete={(v: any) => handleDelete(v.id)}
            />

            <RootDialogPage
                open={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                title={editingTrip ? t('parc.trips.editTrip') : t('parc.trips.newTrip')}
                onAction={handleSave}
            >
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={t('parc.trips.tripTitle')}
                                value={formData.title || ''}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required>
                                <InputLabel>{t('parc.trips.vehicle')}</InputLabel>
                                <Select
                                    value={formData.vehicleId || ''}
                                    label={t('parc.trips.vehicle')}
                                    onChange={(e) => setFormData({ ...formData, vehicleId: Number(e.target.value) })}
                                >
                                    {vehicles.map(v => (
                                        <MenuItem key={v.id} value={v.id}>{v.registrationNumber} ({v.brand})</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required>
                                <InputLabel>{t('parc.trips.driver')}</InputLabel>
                                <Select
                                    value={formData.driverId || ''}
                                    label={t('parc.trips.driver')}
                                    onChange={(e) => setFormData({ ...formData, driverId: Number(e.target.value) })}
                                >
                                    {drivers.map(d => (
                                        <MenuItem key={d.id} value={d.id}>{d.licenseNumber}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>{t('parc.trips.purpose')}</InputLabel>
                                <Select
                                    value={formData.purpose || 'business'}
                                    label={t('parc.trips.purpose')}
                                    onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                                >
                                    <MenuItem value="business">{t('parc.trips.purposeBusiness')}</MenuItem>
                                    <MenuItem value="personal">{t('parc.trips.purposePersonal')}</MenuItem>
                                    <MenuItem value="maintenance">{t('parc.trips.purposeMaintenance')}</MenuItem>
                                    <MenuItem value="delivery">{t('parc.trips.purposeDelivery')}</MenuItem>
                                    <MenuItem value="other">{t('parc.trips.purposeOther')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>{t('parc.trips.status')}</InputLabel>
                                <Select
                                    value={formData.status || 'scheduled'}
                                    label={t('parc.trips.status')}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                >
                                    <MenuItem value="scheduled">{t('parc.trips.statusScheduled')}</MenuItem>
                                    <MenuItem value="in_progress">{t('parc.trips.statusInProgress')}</MenuItem>
                                    <MenuItem value="completed">{t('parc.trips.statusCompleted')}</MenuItem>
                                    <MenuItem value="cancelled">{t('parc.trips.statusCancelled')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
            </RootDialogPage>
        </GenericPage>
    );
};

export default Trips;
